/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { ChevronDown, Search, Server } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import axios from "axios";
import { toast, Toaster } from "react-hot-toast";
import config from "../config";

export default function BuyNumbersPage() {
  const [price, setPrice] = useState(null);
  const [serviceList, setServiceList] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [serviceSearch, setServiceSearch] = useState("");
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [orderProcessing, setOrderProcessing] = useState(false); // ✅ New state for order processing
  const serviceDropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch services on mount
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(
          `${config.API_BASE_URL}/api/auth/fetchUSAServices`,
        );

        const data = res.data?.data || [];
        const mapped = data.map((s) => ({
          id: s.id,
          name: s.name,
          amount: s.amount, // already includes profit from backend
        }));

        mapped.sort((a, b) => a.name.localeCompare(b.name));
        setServiceList(mapped);
      } catch (error) {
        console.error("❌ Error fetching services:", error);
        setServiceList([]);
      }
    };

    fetchServices();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        serviceDropdownRef.current &&
        !serviceDropdownRef.current.contains(event.target)
      ) {
        setShowServiceDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const createOrder = async (service) => {
    if (!service) {
      toast.error("⚠️ Please select a service to proceed.");
      return;
    }

    // ✅ Set processing state to true - this disables the button
    setOrderProcessing(true);

    try {
      toast.loading("📦 Creating order...");

      // Get token from localStorage
      const token = localStorage.getItem("authToken");

      // Make the request with Bearer token
      const res = await axios.post(
        `${config.API_BASE_URL}/api/auth/buyUSActivationNumber`,
        {
          serviceId: service.id,
          serviceName: service.name,
          price: service.amount,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          // REMOVE: withCredentials: true
        },
      );

      toast.dismiss();

      if (res.data.success) {
        const { data } = res.data;
        toast.success(
          `🎉 Order created successfully! Phone: ${data.phoneNumber}`,
        );

        setTimeout(() => {
          navigate("/dashboard/SmsOrder");
        }, 1000);
      } else {
        toast.error(res.data.message || "❌ Failed to create order.");
        // ✅ Only re-enable button if there's an error
        setOrderProcessing(false);
      }
    } catch (err) {
      toast.dismiss();
      console.error(
        "❌ Error creating order:",
        err.response?.data || err.message,
      );

      // Handle specific error cases
      if (err.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("authToken");
        navigate("/login");
      } else {
        toast.error(
          err.response?.data?.message || "❌ Failed to create order.",
        );
      }

      // ✅ Re-enable button on error so user can try again
      setOrderProcessing(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Toaster position="top-center" />
      <Sidebar />
      <MobileHeader />

      <div className="flex-1 flex flex-col mt-[70px] lg:mt-0">
        <main className="p-4 md:p-8 flex-1 w-full max-w-full overflow-x-hidden">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 md:mb-8">
            Receive US SMS
          </h1>

          {/* Service Dropdown */}
          <div ref={serviceDropdownRef} className="mb-6 relative mt-[20px]">
            <h2 className="text-lg font-semibold mb-2">Select Service</h2>

            <div
              className={`relative bg-white border rounded-lg px-3 py-2 md:px-4 md:py-3 flex justify-between items-center w-full transition ${
                orderProcessing
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer hover:border-gray-400"
              }`}
              onClick={() =>
                !orderProcessing && setShowServiceDropdown(!showServiceDropdown)
              }
            >
              <span>
                {selectedService?.name
                  ? selectedService.name
                  : "Choose a service..."}
              </span>
              <ChevronDown className="w-5 h-5 text-gray-500" />
            </div>

            {showServiceDropdown && !orderProcessing && (
              <div className="absolute mt-2 bg-white border rounded-lg shadow-lg w-full z-50">
                <div className="flex items-center bg-gray-100 rounded-t-lg px-3 py-2">
                  <Search className="w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search services..."
                    value={serviceSearch}
                    onChange={(e) => setServiceSearch(e.target.value)}
                    className="bg-transparent outline-none px-2 w-full text-gray-700"
                  />
                </div>

                <ul className="max-h-60 overflow-y-auto">
                  {serviceList
                    .filter((s) =>
                      s.name
                        .toLowerCase()
                        .includes(serviceSearch.toLowerCase()),
                    )
                    .map((service, index) => (
                      <li
                        key={index}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-sm md:text-base transition"
                        onClick={() => {
                          setSelectedService(service);
                          setPrice(service.amount);
                          setShowServiceDropdown(false);
                        }}
                      >
                        <Server className="w-4 h-4 text-blue-500" />
                        <span>{service.name}</span>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>

          {/* Purchase Button */}
          {selectedService && (
            <div className="mt-6">
              <button
                disabled={!price || orderProcessing}
                onClick={() => createOrder(selectedService)}
                className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold shadow transition flex items-center justify-center gap-2 ${
                  !price || orderProcessing
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-[#007AFF] hover:bg-[#005FCC] text-white"
                }`}
              >
                {orderProcessing ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing Order...
                  </>
                ) : price ? (
                  `Purchase ₦${price}`
                ) : (
                  "Select a service to continue"
                )}
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
